/**
 * SearchInput — el buscador de una tabla o un listado.
 *
 * ⚠️ `type="search"`, no `type="text"`: el navegador agrega la cruz de borrar y
 * el teclado del teléfono muestra la tecla «Buscar» en vez de «Intro».
 *
 * ⚠️ `label` sale como `aria-label` y **no es el placeholder**. Un placeholder
 * desaparece al escribir, así que un campo cuyo único rótulo es el placeholder
 * queda sin nombre justo cuando tiene contenido — que es cuando alguien vuelve
 * a él para corregir.
 */
import type { CSSProperties, HTMLAttributes, InputHTMLAttributes } from 'react'

export interface SearchInputProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style' | 'onChange'> {
  value?: string
  onChange?: InputHTMLAttributes<HTMLInputElement>['onChange']
  placeholder?: string
  /** El nombre accesible del campo. Sobrevive a que se escriba algo. */
  label?: string
  disabled?: boolean
  className?: string
  style?: CSSProperties
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Buscar',
  label = 'Buscar',
  disabled = false,
  className = '',
  style,
  ...rest
}: SearchInputProps) {
  return (
    <div
      className={`cl-inputwrap ${className}`.trim()}
      style={{ height: 'var(--control-h)', ...style }}
      {...rest}
    >
      <svg
        aria-hidden="true"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="11" cy="11" r="7" />
        <line x1="16.5" y1="16.5" x2="21" y2="21" />
      </svg>
      <input
        type="search"
        aria-label={label}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  )
}
